import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowLeft, Mail, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/utils";
import { toast } from "sonner";

const SHOP_EMAIL = "shop-2026@outlook.fr";

export default function Checkout() {
  const { items, subtotal, shipping, total, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    address: "", city: "", zip: "", notes: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMailto = (orderId) => {
    const lines = items.map((i) => `• ${i.name} (${[i.color, i.size].filter(Boolean).join(", ")}) x${i.quantity} — ${formatPrice(i.price * i.quantity)}`).join("%0D%0A");
    const body =
      `Bonjour,%0D%0A%0D%0AJe souhaite passer la commande suivante (${orderId}) :%0D%0A%0D%0A` +
      `${lines}%0D%0A%0D%0A` +
      `Sous-total : ${formatPrice(subtotal)}%0D%0ALivraison : ${shipping === 0 ? "Offerte" : formatPrice(shipping)}%0D%0ATotal : ${formatPrice(total)}%0D%0A%0D%0A` +
      `Client : ${form.name}%0D%0AEmail : ${form.email}%0D%0ATéléphone : ${form.phone}%0D%0A` +
      `Adresse : ${form.address}, ${form.zip} ${form.city}%0D%0A` +
      (form.notes ? `Notes : ${form.notes}%0D%0A` : "") +
      `%0D%0AMerci !`;
    return `mailto:${SHOP_EMAIL}?subject=${encodeURIComponent(`Nouvelle commande ${orderId} - shop-canapé`)}&body=${body}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ productId: i.productId, name: i.name, color: i.color, size: i.size, quantity: i.quantity, price: i.price, image: i.image })),
        subtotal, shipping, total, customer: form,
      });
      const mailto = buildMailto(data.order_id);
      window.location.href = mailto;
      setDone({ orderId: data.order_id, mailto });
      clearCart();
      toast.success("Commande enregistrée ! Votre email s'ouvre pour nous contacter.");
    } catch {
      toast.error("Impossible d'enregistrer la commande.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-32 text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
          <CheckCircle2 size={72} className="text-emerald-500" />
        </motion.div>
        <h1 className="mt-6 font-serif text-4xl">Merci pour votre commande !</h1>
        <p className="mt-2 text-sm text-muted-foreground">Commande <span className="font-semibold text-foreground">{done.orderId}</span> enregistrée.</p>
        <p className="mt-4 text-muted-foreground">Votre logiciel de messagerie s'est ouvert pour envoyer les détails à <span className="font-semibold">{SHOP_EMAIL}</span>. Si ce n'est pas le cas, cliquez ci-dessous.</p>
        <a href={done.mailto} className="mt-6 flex items-center gap-2 rounded-full bg-gold-gradient px-7 py-3.5 text-sm font-semibold text-obsidian" data-testid="mailto-button">
          <Mail size={16} /> Nous contacter par email
        </a>
        <Link to="/canapes" className="mt-4 text-sm text-muted-foreground underline">Continuer mes achats</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <h1 className="font-serif text-3xl">Votre panier est vide</h1>
        <Link to="/canapes" className="mt-6 rounded-full bg-obsidian px-6 py-3 text-sm font-semibold text-cream">Découvrir la collection</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
      <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Retour</button>
      <h1 className="mb-10 font-serif text-4xl font-medium lg:text-5xl">Finaliser la commande</h1>

      <div className="grid gap-12 lg:grid-cols-[1fr_400px]">
        <form onSubmit={submit} className="space-y-5" data-testid="checkout-form">
          <h2 className="font-serif text-2xl">Vos coordonnées</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom complet" required value={form.name} onChange={set("name")} testid="checkout-name" />
            <Field label="Email" type="email" required value={form.email} onChange={set("email")} testid="checkout-email" />
            <Field label="Téléphone" required value={form.phone} onChange={set("phone")} testid="checkout-phone" />
            <Field label="Code postal" required value={form.zip} onChange={set("zip")} testid="checkout-zip" />
          </div>
          <Field label="Adresse" required value={form.address} onChange={set("address")} testid="checkout-address" />
          <Field label="Ville" required value={form.city} onChange={set("city")} testid="checkout-city" />
          <div>
            <label className="mb-1.5 block text-sm font-medium">Notes (optionnel)</label>
            <textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="checkout-notes"
              className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-gold" />
          </div>
          <div className="rounded-2xl bg-secondary/60 p-4 text-sm text-muted-foreground">
            <Mail size={16} className="mb-1 inline text-gold-dark" /> En validant, votre commande nous sera envoyée directement par email à <span className="font-semibold text-foreground">{SHOP_EMAIL}</span> pour confirmation et paiement.
          </div>
          <button type="submit" disabled={submitting} data-testid="place-order-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-obsidian py-4 text-sm font-semibold text-cream transition-colors hover:bg-gold hover:text-obsidian disabled:opacity-60">
            {submitting ? <Loader2 size={17} className="animate-spin" /> : <Mail size={17} />}
            {submitting ? "Traitement…" : "Valider et nous contacter"}
          </button>
        </form>

        <aside className="h-fit rounded-3xl border border-border bg-card p-6 shadow-soft lg:sticky lg:top-28">
          <h2 className="mb-5 font-serif text-2xl">Récapitulatif</h2>
          <div className="max-h-72 space-y-4 overflow-y-auto">
            {items.map((i, idx) => (
              <div key={idx} className="flex gap-3" data-testid={`summary-item-${i.productId}`}>
                <img src={i.image} alt="" className="h-16 w-16 rounded-xl object-cover" />
                <div className="flex-1 text-sm">
                  <p className="font-serif text-base leading-tight">{i.name}</p>
                  <p className="text-xs text-muted-foreground">{[i.color, i.size].filter(Boolean).join(" · ")} · x{i.quantity}</p>
                </div>
                <span className="text-sm font-semibold">{formatPrice(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-2 border-t border-border pt-4 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Sous-total</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Livraison</span><span>{shipping === 0 ? "Offerte" : formatPrice(shipping)}</span></div>
            <div className="flex justify-between border-t border-border pt-2 text-lg font-semibold"><span>Total</span><span data-testid="checkout-total">{formatPrice(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, testid, required, type = "text", value, onChange }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium">{label}{required && <span className="text-gold-dark"> *</span>}</label>
      <input type={type} required={required} value={value} onChange={onChange} data-testid={testid}
        className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-gold" />
    </div>
  );
}
